import {
  ArrowLeft,
  ArrowRight,
  Download,
  RefreshCw,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { presenterApi } from '../lib/presenterApi';
import { exportPresenterResults } from '../lib/exportResults';
import type { EventSnapshot } from '../types';

function changedTime(value: string | null): string {
  if (!value) return 'Not set yet';
  return new Intl.DateTimeFormat('en', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(new Date(value));
}

export function PresenterView() {
  const [snapshot, setSnapshot] = useState<EventSnapshot | null>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('Connecting…');
  const [error, setError] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportInProgress = useRef(false);
  const busy = saving || exporting;

  const currentIndex = useMemo(
    () =>
      snapshot?.topics.findIndex(
        (topic) => topic.id === snapshot.settings.currentTopicId,
      ) ?? -1,
    [snapshot],
  );
  const currentTopic =
    currentIndex >= 0 ? snapshot?.topics[currentIndex] ?? null : null;

  const applyResponse = useCallback(
    (result: Awaited<ReturnType<typeof presenterApi.status>>) => {
      setSnapshot(result.snapshot);
      setError(null);
      setStatus(`Updated ${new Intl.DateTimeFormat('en', {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      }).format(new Date())}`);
    },
    [],
  );

  const run = useCallback(
    async (
      action: () => ReturnType<typeof presenterApi.status>,
      pendingLabel: string,
    ) => {
      if (busy) return;
      setSaving(true);
      setStatus(pendingLabel);
      try {
        applyResponse(await action());
      } catch (requestError) {
        setError(
          requestError instanceof Error ? requestError.message : 'Request failed.',
        );
        setStatus('Update failed');
      } finally {
        setSaving(false);
      }
    },
    [applyResponse, busy],
  );

  const exportResults = async () => {
    if (busy || exportInProgress.current) return;
    exportInProgress.current = true;
    setExporting(true);
    setExportStatus('Preparing Excel export…');
    setExportError(null);
    try {
      await exportPresenterResults();
      setExportStatus('Excel file downloaded.');
    } catch (exportError) {
      setExportStatus(null);
      setExportError(exportError instanceof Error
        ? `Export failed: ${exportError.message}`
        : 'Export failed. Check your connection and try again.');
    } finally {
      exportInProgress.current = false;
      setExporting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setSaving(true);
    presenterApi
      .status()
      .then((result) => {
        if (!cancelled) applyResponse(result);
      })
      .catch((requestError: unknown) => {
        if (cancelled) return;
        setError(
          requestError instanceof Error ? requestError.message : 'Request failed.',
        );
        setStatus('Connection failed');
      })
      .finally(() => {
        if (!cancelled) setSaving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applyResponse]);

  if (!snapshot) {
    return (
      <main className="presenter-loading">
        <img src="/signify-logo.svg" alt="Signify" />
        <h1>Presenter control</h1>
        <p>{saving ? 'Loading presenter controls…' : status}</p>
        {!saving && (
          <button
            className="button primary"
            type="button"
            onClick={() => void run(presenterApi.status, 'Connecting…')}
          >
            Try again
          </button>
        )}
        {error && <div className="presenter-error" role="alert">{error}</div>}
      </main>
    );
  }

  const selectTopic = (topicId: string | null) =>
    run(
      () => presenterApi.update({ currentTopicId: topicId }),
      'Publishing current topic…',
    );

  const returnAudienceToTopicList = () =>
    run(
      () =>
        presenterApi.update({
          currentTopicId: null,
          audienceBrowsingEnabled: true,
        }),
      'Opening topic list for the audience…',
    );

  const returnAudienceToWelcome = () =>
    run(
      () =>
        presenterApi.update({
          currentTopicId: null,
          audienceBrowsingEnabled: false,
        }),
      'Returning the audience to the welcome page…',
    );

  return (
    <main className="presenter-page">
      <header className="presenter-header">
        <div className="presenter-brand">
          <img src="/signify-logo.svg" alt="Signify" />
          <div>
            <strong>{snapshot.settings.eventTitle}</strong>
            <span>Presenter control · Link access</span>
          </div>
        </div>
      </header>

      <section className="presenter-current" aria-labelledby="presenter-current-title">
        <div className="presenter-state">
          <span className={error ? 'is-error' : 'is-online'}>
            {error ? <WifiOff /> : <Wifi />}
            {status}
          </span>
          <button
            className="icon-button"
            type="button"
            disabled={busy}
            aria-label="Refresh presenter status"
            onClick={() => void run(presenterApi.status, 'Refreshing…')}
          >
            <RefreshCw aria-hidden="true" />
          </button>
        </div>
        <h1 id="presenter-current-title">
          {currentTopic?.title ?? 'No topic is live'}
        </h1>
        <p>
          {currentTopic?.presenter ?? 'Choose a published topic when the room is ready.'}
        </p>
        <dl>
          <div>
            <dt>Changed</dt>
            <dd>{changedTime(snapshot.settings.currentTopicChangedAt)}</dd>
          </div>
          <div>
            <dt>Audience follow</dt>
            <dd>{snapshot.settings.audienceFollowEnabled ? 'On' : 'Paused'}</dd>
          </div>
        </dl>
      </section>

      {error && <div className="presenter-error" role="alert">{error}</div>}

      <section className="presenter-controls" aria-label="Topic controls">
        <div className="presenter-step-controls">
          <button
            className="button secondary"
            type="button"
            disabled={busy || currentIndex <= 0}
            onClick={() => void selectTopic(snapshot.topics[currentIndex - 1].id)}
          >
            <ArrowLeft aria-hidden="true" />
            Previous
          </button>
          <button
            className="button primary"
            type="button"
            disabled={busy || currentIndex >= snapshot.topics.length - 1}
            onClick={() =>
              void selectTopic(snapshot.topics[Math.max(0, currentIndex + 1)].id)
            }
          >
            Next
            <ArrowRight aria-hidden="true" />
          </button>
        </div>

        <label htmlFor="presenter-topic">Published topic</label>
        <select
          id="presenter-topic"
          value={snapshot.settings.currentTopicId ?? ''}
          disabled={busy}
          onChange={(event) => void selectTopic(event.target.value || null)}
        >
          <option value="">No current topic</option>
          {snapshot.topics.map((topic) => (
            <option key={topic.id} value={topic.id}>
              {topic.displayOrder}. {topic.title}
              {topic.status === 'closed' ? ' (closed)' : ''}
            </option>
          ))}
        </select>
        <p>
          Closed published topics may be presented and viewed, but audience
          interactions remain unavailable until the topic is open.
        </p>

        <div className="presenter-toggle-row">
          <div>
            <strong>Audience follow</strong>
            <span>Show the countdown and move following attendees to the live topic.</span>
          </div>
          <label className="switch-control">
            <input
              type="checkbox"
              checked={snapshot.settings.audienceFollowEnabled}
              disabled={busy}
              onChange={(event) =>
                void run(
                  () =>
                    presenterApi.update({
                      audienceFollowEnabled: event.target.checked,
                    }),
                  'Updating audience follow…',
                )
              }
            />
            <span aria-hidden="true" />
            {snapshot.settings.audienceFollowEnabled ? 'On' : 'Off'}
          </label>
        </div>

        <button
          className="button secondary clear-current"
          type="button"
          disabled={
            busy ||
            (!snapshot.settings.currentTopicId &&
              !snapshot.settings.audienceBrowsingEnabled)
          }
          onClick={() => void returnAudienceToWelcome()}
        >
          Return audience to welcome
        </button>
        <button
          className="button primary return-topic-list"
          type="button"
          disabled={busy}
          onClick={() => void returnAudienceToTopicList()}
        >
          Return audience to topic list
        </button>
        <p>
          Open the full topic list at any time. It stays open until you present
          another topic or return the audience to welcome. Attendees can browse
          topics and the leaderboard freely.
        </p>
      </section>

      <section className="presenter-controls presenter-export" aria-labelledby="export-title">
        <h2 id="export-title">Export meeting data</h2>
        <p>
          Download an Excel file with all topics, like and flower totals, and every
          comment, including hidden comments and unpublished topics.
          Only data already saved on the server is included. Export before resetting.
        </p>
        <button
          className="button secondary"
          type="button"
          disabled={busy}
          onClick={() => void exportResults()}
        >
          <Download aria-hidden="true" />
          {exporting ? 'Exporting…' : 'Export all meeting data'}
        </button>
        {exportStatus && <div role="status" aria-live="polite">{exportStatus}</div>}
        {exportError && <div className="presenter-error" role="alert">{exportError}</div>}
      </section>

      <section className="presenter-controls presenter-reset" aria-labelledby="reset-title">
        <h2 id="reset-title">Reset meeting</h2>
        <p>
          Delete all likes, flowers, and comments, including hidden comments.
          Return everyone to the welcome page and prepare topics for a new meeting.
          Topic content is preserved. This cannot be undone. Export results first.
        </p>
        {!confirmReset ? (
          <button
            className="button secondary"
            type="button"
            disabled={busy}
            onClick={() => setConfirmReset(true)}
          >
            Clear all meeting data
          </button>
        ) : (
          <div className="reset-confirmation" role="group" aria-label="Confirm meeting reset">
            <p role="alert">Permanently delete all audience interactions?</p>
            <button
              className="button primary"
              type="button"
              disabled={busy}
              onClick={() => void run(async () => {
                const result = await presenterApi.reset();
                setConfirmReset(false);
                return result;
              }, 'Clearing all meeting data…')}
            >
              Delete all data and reset meeting
            </button>
            <button
              className="button secondary"
              type="button"
              autoFocus
              disabled={busy}
              onClick={() => setConfirmReset(false)}
            >
              Cancel
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
